import { API_BASE } from "./config/api";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Steps,
  Tabs,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  IdcardOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  SafetyOutlined,
  UserOutlined,
  WhatsAppOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./ClientLogin.css";
import { clearClientSession, clearDistributorSession } from "./config/auth";
import {
  enableBiometricLogin,
  getBiometricCapability,
  getBiometricAccountHint,
  hasBiometricLogin,
  isBiometricCancellation,
  isNativeBiometricRealm,
  readBiometricLogin,
} from "./config/biometricAuth";
import { captureRequiredClientLoginLocation } from "./config/clientLoginLocation";

const { Title, Text } = Typography;


function ClientLogin({ embedded = false }) {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("signin");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [biometricOffer, setBiometricOffer] = useState(null);
  const [showCredentialLogin, setShowCredentialLogin] = useState(true);
  const [activeSessionPrompt, setActiveSessionPrompt] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isNativeBiometricRealm("client")) return;
      const capability = await getBiometricCapability("client");
      const enabled = capability.available ? await hasBiometricLogin("client") : false;
      if (alive) {
        setBiometricAvailable(capability.available);
        setBiometricEnabled(enabled);
        setShowCredentialLogin(!enabled);
        if (enabled) {
          window.setTimeout(() => handleBiometricLogin(true), 150);
        }
      }
    })();
    return () => { alive = false; };
  }, []);

  const finishClientLogin = (token, userId = "", clientName = "") => {
    clearDistributorSession();
    clearClientSession();
    sessionStorage.setItem("client_auth_token", token);
    localStorage.removeItem("client_auth_token");
    localStorage.setItem("client_user_id", userId);
    localStorage.setItem("client_name", clientName);
    window.location.assign("/client/dashboard");
  };

  const handleBiometricLogin = async (autoStart = false) => {
    setLoginError("");
    setBiometricLoading(true);
    try {
      const saved = await readBiometricLogin("client");
      if (!saved?.deviceCredential) {
        setBiometricEnabled(false);
        setLoginError("Biometric login is not configured. Please sign in with User ID and password.");
        return;
      }

      const loginLocation = await captureRequiredClientLoginLocation();
      const response = await fetch(`${API_BASE}/client_biometric_exchange.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-KG-Biometric-Authorization": `Bearer ${saved.deviceCredential}`,
        },
        body: JSON.stringify({ location: loginLocation }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status !== "ok" || !data.token) {
        throw new Error(data.message || "Biometric session exchange failed.");
      }

      finishClientLogin(
        data.token,
        data.client?.user_id || saved.userId || "",
        data.client?.client_name || saved.displayName || ""
      );
    } catch (error) {
      if (isBiometricCancellation(error)) {
        if (autoStart) setShowCredentialLogin(true);
      } else {
        setShowCredentialLogin(true);
        setLoginError("Biometric login could not be completed. Please use User ID and password.");
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const acceptBiometricOffer = async () => {
    if (!biometricOffer) return;
    setBiometricLoading(true);
    try {
      const response = await fetch(`${API_BASE}/client_biometric_enroll.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-KG-Client-Authorization": `Bearer ${biometricOffer.token}`,
        },
        body: JSON.stringify({ device_label: "MFSetu - MyWealth" }),
      });
      const data = await response.json().catch(() => ({}));
      const deviceCredential = data?.device_credential;
      if (!response.ok || data.status !== "ok" || !deviceCredential) {
        throw new Error(data.message || "Biometric enrollment failed.");
      }

      await enableBiometricLogin("client", {
        deviceCredential,
        userId: biometricOffer.userId,
        displayName: biometricOffer.displayName,
      });
      setBiometricEnabled(true);
      finishClientLogin(biometricOffer.token, biometricOffer.userId, biometricOffer.displayName);
    } catch (error) {
      if (!isBiometricCancellation(error)) {
        setLoginError("Biometric setup could not be completed. You can enable it later from the menu.");
      }
      finishClientLogin(biometricOffer.token, biometricOffer.userId, biometricOffer.displayName);
    } finally {
      setBiometricLoading(false);
      setBiometricOffer(null);
    }
  };

  // Forgot Password flow
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState(0);
  const [forgotUserId, setForgotUserId] = useState("");
  const [forgotMethod, setForgotMethod] = useState("BOTH");
  const [forgotRequestId, setForgotRequestId] = useState(null);
  const [forgotDestination, setForgotDestination] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotResetToken, setForgotResetToken] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotResendSeconds, setForgotResendSeconds] = useState(0);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  const [signupStep, setSignupStep] = useState(0);
  const [pan, setPan] = useState("");
  const [panLoading, setPanLoading] = useState(false);
  const [panWarning, setPanWarning] = useState("");
  const [existingLoginPopup, setExistingLoginPopup] = useState({ open: false, clientName: "" });
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  const [customUserId, setCustomUserId] = useState(false);
  const [userId, setUserId] = useState("");
  const [userIdAvailable, setUserIdAvailable] = useState(false);
  const [userIdLoading, setUserIdLoading] = useState(false);

  const [signupPassword, setSignupPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [mobileOtp, setMobileOtp] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [whatsappOtp, setWhatsappOtp] = useState("");

  const [mobileOtpSent, setMobileOtpSent] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [whatsappOtpSent, setWhatsappOtpSent] = useState(false);

  const [mobileVerified, setMobileVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [whatsappVerified, setWhatsappVerified] = useState(false);

  const [whatsappSame, setWhatsappSame] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState("");

  const [signupLoading, setSignupLoading] = useState(false);

  const [signupSuccessPopup, setSignupSuccessPopup] = useState(false);

  const postJson = async (url, body) => {
    const response = await fetch(`${API_BASE}/${url}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const raw = await response.text();
    try {
      return JSON.parse(raw);
    } catch {
      throw new Error(`Invalid response from ${url}`);
    }
  };

  useEffect(() => {
    if (!forgotOpen || forgotResendSeconds <= 0) return undefined;

    const timer = window.setInterval(() => {
      setForgotResendSeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [forgotOpen, forgotResendSeconds]);

  const resetForgotPassword = () => {
    setForgotStep(0);
    setForgotUserId("");
    setForgotMethod("BOTH");
    setForgotRequestId(null);
    setForgotDestination("");
    setForgotOtp("");
    setForgotResetToken("");
    setForgotNewPassword("");
    setForgotConfirmPassword("");
    setForgotLoading(false);
    setForgotError("");
    setForgotResendSeconds(0);
    setForgotSuccess(false);
  };

  const openForgotPassword = () => {
    resetForgotPassword();
    setForgotOpen(true);
  };

  const closeForgotPassword = () => {
    setForgotOpen(false);
    resetForgotPassword();
  };

  const sendForgotOtp = async ({ resend = false } = {}) => {
    const finalUserId = forgotUserId.trim();

    if (!finalUserId) {
      setForgotError("Please enter your User ID.");
      return;
    }

    if (!["MOBILE", "EMAIL", "BOTH"].includes(forgotMethod)) {
      setForgotError("Please select Mobile, Email or Both.");
      return;
    }

    if (resend && forgotResendSeconds > 0) return;

    setForgotLoading(true);
    setForgotError("");

    try {
      const data = await postJson("client_forgot_password.php", {
        user_id: finalUserId,
        verification_type: forgotMethod,
      });

      if (data.status !== "ok") {
        if (data.code === "OTP_RESEND_WAIT" && data.retry_after_seconds) {
          setForgotResendSeconds(Number(data.retry_after_seconds) || 30);
        }
        setForgotError(data.message || "Unable to send OTP.");
        return;
      }

      /*
       * For a valid account the backend returns request_id.
       * For an unknown/inactive User ID it intentionally returns a generic
       * accepted response without revealing whether the account exists.
       */
      if (!data.request_id) {
        setForgotError(
          data.message ||
            "If the account details are valid, an OTP will be sent to the registered contact."
        );
        return;
      }

      setForgotRequestId(Number(data.request_id));
      setForgotDestination(data.destination || "");
      setForgotOtp("");
      setForgotResetToken("");
      setForgotResendSeconds(Number(data.resend_after_seconds) || 30);
      setForgotStep(1);

      if (resend) {
        message.success("A new OTP has been sent.");
      } else {
        message.success("OTP sent successfully.");
      }
    } catch (error) {
      setForgotError(error.message || "Unable to send OTP.");
    } finally {
      setForgotLoading(false);
    }
  };

  const verifyForgotOtp = async () => {
    if (!forgotRequestId) {
      setForgotError("Please request a new OTP.");
      return;
    }

    if (!/^\d{6}$/.test(forgotOtp)) {
      setForgotError("Please enter a valid 6-digit OTP.");
      return;
    }

    setForgotLoading(true);
    setForgotError("");

    try {
      const data = await postJson("client_verify_reset_otp.php", {
        request_id: forgotRequestId,
        otp: forgotOtp,
      });

      if (data.status !== "ok" || !data.reset_token) {
        setForgotError(data.message || "OTP verification failed.");
        return;
      }

      // Keep the one-time reset token only in component memory.
      setForgotResetToken(data.reset_token);
      setForgotNewPassword("");
      setForgotConfirmPassword("");
      setForgotStep(2);
      message.success("OTP verified successfully.");
    } catch (error) {
      setForgotError(error.message || "Unable to verify OTP.");
    } finally {
      setForgotLoading(false);
    }
  };

  const completeForgotPassword = async () => {
    if (!forgotRequestId || !forgotResetToken) {
      setForgotError("Password reset authorization has expired. Please start again.");
      return;
    }

    if (forgotNewPassword.length < 8) {
      setForgotError("Password must be at least 8 characters.");
      return;
    }
    if (!/[A-Z]/.test(forgotNewPassword)) {
      setForgotError("Password must contain one uppercase letter.");
      return;
    }
    if (!/[a-z]/.test(forgotNewPassword)) {
      setForgotError("Password must contain one lowercase letter.");
      return;
    }
    if (!/[0-9]/.test(forgotNewPassword)) {
      setForgotError("Password must contain one number.");
      return;
    }
    if (!/[^A-Za-z0-9]/.test(forgotNewPassword)) {
      setForgotError("Password must contain one special character.");
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotError("New Password and Confirm Password do not match.");
      return;
    }

    setForgotLoading(true);
    setForgotError("");

    try {
      const data = await postJson("client_complete_forgot_password.php", {
        request_id: forgotRequestId,
        reset_token: forgotResetToken,
        new_password: forgotNewPassword,
        confirm_password: forgotConfirmPassword,
      });

      if (data.status !== "ok") {
        setForgotError(data.message || "Unable to change password.");
        return;
      }

      // Never retain reset credentials after successful use.
      setForgotResetToken("");
      setForgotOtp("");
      setForgotNewPassword("");
      setForgotConfirmPassword("");
      setForgotSuccess(true);
      setForgotStep(3);
    } catch (error) {
      setForgotError(error.message || "Unable to change password.");
    } finally {
      setForgotLoading(false);
    }
  };

  const completeCredentialLogin = async (values, forceLogin = false, loginLocation = null) => {
    const location = loginLocation || await captureRequiredClientLoginLocation();
    const data = await postJson("client_login.php", {
      user_id: values.user_id,
      password: values.password,
      force_login: forceLogin,
      location,
    });

    if (data.status === "confirmation_required" || data.code === "ACTIVE_SESSION_EXISTS") {
      setActiveSessionPrompt({
        values,
        location,
        message:
          data.message ||
          "Your account is already logged in on another device. Do you want to log out the previous session and continue here?",
      });
      return;
    }

    if (data.status !== "ok" || !data.token) {
      setLoginError(data.message || "Invalid User ID or Password.");
      return;
    }

    const loginUserId = data.client?.user_id || values.user_id || "";
    const loginClientName = data.client?.client_name || "";

    if (
      biometricAvailable &&
      (!biometricEnabled || getBiometricAccountHint("client") !== loginUserId)
    ) {
      setBiometricOffer({
        token: data.token,
        userId: loginUserId,
        displayName: loginClientName,
      });
      return;
    }

    finishClientLogin(data.token, loginUserId, loginClientName);
  };

  const forceLoginFromPrompt = async () => {
    const pending = activeSessionPrompt;
    if (!pending) return;

    setActiveSessionPrompt(null);
    setLoginLoading(true);
    setLoginError("");

    try {
      await completeCredentialLogin(pending.values, true, pending.location);
    } catch (error) {
      setLoginError(error.message || "Unable to connect to server.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignIn = async (values) => {
    setLoginLoading(true);
    setLoginError("");

    try {
      await completeCredentialLogin(values, false);
    } catch (error) {
      setLoginError(error.message || "Unable to connect to server.");
    } finally {
      setLoginLoading(false);
    }
  };

  const resetOtpState = () => {
    setMobileOtp("");
    setEmailOtp("");
    setWhatsappOtp("");
    setMobileOtpSent(false);
    setEmailOtpSent(false);
    setWhatsappOtpSent(false);
    setMobileVerified(false);
    setEmailVerified(false);
    setWhatsappVerified(false);
  };

  const verifyPan = async () => {
    const normalizedPan = pan.trim().toUpperCase();

    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(normalizedPan)) {
      message.error("Please enter a valid PAN.");
      return;
    }

    setPanWarning("");
    setPanLoading(true);

    try {
      const data = await postJson("verify_client_pan.php", {
        pan: normalizedPan,
      });

      // PAN exists, but login is already created.
      if (data.code === "LOGIN_ALREADY_EXISTS" || data.status === "already_registered") {
        const existingClient = data.client || {};
        const clientName =
          existingClient.client_name ||
          existingClient.invester_name ||
          existingClient.name ||
          "Client";

        setExistingLoginPopup({ open: true, clientName });
        return;
      }

      // PAN itself is not present in client_master.
      if (data.code === "PAN_NOT_FOUND") {
        setPanWarning("This PAN does not exist in our records. Please contact your distributor.");
        return;
      }

      if (data.status !== "ok") {
        setPanWarning(data.message || "PAN verification failed.");
        return;
      }

      let list = [];
      if (data.client) list = [data.client];
      else if (Array.isArray(data.clients)) list = data.clients;
      else if (Array.isArray(data.distributors)) list = data.distributors;
      else if (Array.isArray(data.arn_options)) list = data.arn_options;

      if (!list.length) {
        message.error(data.message || "No distributor found for this PAN.");
        return;
      }

      setPan(normalizedPan);
      setClients(list);
      setUserId(normalizedPan);
      setCustomUserId(false);
      setUserIdAvailable(false);
      resetOtpState();

      if (list.length === 1) {
        if (list[0].login_exists) {
          Modal.info({
            title: "Account Already Registered",
            content: "A login already exists for this PAN and distributor. Please Sign In.",
            okText: "Go to Sign In",
            onOk: () => setActiveTab("signin"),
          });
          return;
        }

        setSelectedClient(list[0]);
        setSignupStep(2);
      } else {
        setSelectedClient(null);
        setSignupStep(1);
      }
    } catch (error) {
      message.error(error.message || "Unable to verify PAN.");
    } finally {
      setPanLoading(false);
    }
  };

  const handleDistributorSelect = (arnNo) => {
    const found = clients.find((item) => item.arn_no === arnNo);
    if (!found) return;

    if (found.login_exists) {
      message.error("Login is already registered with this distributor.");
      return;
    }

    setSelectedClient(found);
    setSignupStep(2);
    resetOtpState();
  };

  const checkUserId = async (candidate = userId) => {
    const finalUserId = String(candidate || "").trim();

    if (!finalUserId) {
      message.error("Please enter User ID.");
      return false;
    }

    setUserIdLoading(true);

    try {
      const data = await postJson("check_client_userid.php", {
        user_id: finalUserId,
      });

      if (data.available) {
        setUserId(finalUserId);
        setUserIdAvailable(true);
        message.success("User ID is available.");
        return true;
      }

      setUserIdAvailable(false);

      if (Array.isArray(data.suggestions) && data.suggestions.length) {
        message.error(
          `User ID unavailable. Try: ${data.suggestions.join(", ")}`
        );
      } else {
        message.error("This User ID is not available.");
      }

      return false;
    } catch (error) {
      message.error(error.message || "Unable to check User ID.");
      return false;
    } finally {
      setUserIdLoading(false);
    }
  };

  const validatePassword = () => {
    if (signupPassword.length < 8) {
      message.error("Password must be at least 8 characters.");
      return false;
    }
    if (!/[A-Z]/.test(signupPassword)) {
      message.error("Password must contain one uppercase letter.");
      return false;
    }
    if (!/[a-z]/.test(signupPassword)) {
      message.error("Password must contain one lowercase letter.");
      return false;
    }
    if (!/[0-9]/.test(signupPassword)) {
      message.error("Password must contain one number.");
      return false;
    }
    if (!/[^A-Za-z0-9]/.test(signupPassword)) {
      message.error("Password must contain one special character.");
      return false;
    }
    if (signupPassword !== confirmPassword) {
      message.error("Password and Confirm Password do not match.");
      return false;
    }
    return true;
  };

  const continueToContactVerification = async () => {
    if (!selectedClient) {
      message.error("Please select your distributor.");
      return;
    }

    let available = userIdAvailable;

    if (!customUserId && !userIdAvailable) {
      available = await checkUserId(pan.toUpperCase());
      if (!available) {
        setCustomUserId(true);
        setUserId("");
        message.warning("PAN is already used as a User ID. Please choose a different User ID.");
        return;
      }
    }

    if (customUserId && !userIdAvailable) {
      message.error("Please check User ID availability first.");
      return;
    }

    if (!available || !validatePassword()) return;

    setSignupStep(3);
  };

  const sendOtp = async (type) => {
    if (!selectedClient) {
      message.error("Please select distributor first.");
      return;
    }

    if (type === "WHATSAPP" && !whatsappNumber.trim()) {
      message.error("Please enter WhatsApp number.");
      return;
    }

    try {
      const body = {
        pan: pan.toUpperCase(),
        arn_no: selectedClient.arn_no,
        verification_type: type,
      };

      if (type === "WHATSAPP") {
        body.whatsapp_number = whatsappNumber.trim();
      }

      const data = await postJson("send_client_signup_otp.php", body);

      if (data.status !== "ok") {
        message.error(data.message || "Unable to generate OTP.");
        return;
      }

      if (type === "MOBILE") {
        setMobileOtpSent(true);
        setMobileVerified(false);
      }
      if (type === "EMAIL") {
        setEmailOtpSent(true);
        setEmailVerified(false);
      }
      if (type === "WHATSAPP") {
        setWhatsappOtpSent(true);
        setWhatsappVerified(false);
      }

      message.success("OTP sent successfully.");
    } catch (error) {
      message.error(error.message || "Unable to generate OTP.");
    }
  };

  const verifyOtp = async (type, otp) => {
    if (!/^\d{6}$/.test(otp || "")) {
      message.error("Please enter the 6 digit OTP.");
      return;
    }

    try {
      const body = {
        pan: pan.toUpperCase(),
        arn_no: selectedClient.arn_no,
        verification_type: type,
        otp,
      };

      if (type === "WHATSAPP") {
        body.whatsapp_number = whatsappNumber.trim();
      }

      const data = await postJson("verify_client_signup_otp.php", body);

      if (data.status !== "ok") {
        message.error(data.message || "OTP verification failed.");
        return;
      }

      if (type === "MOBILE") setMobileVerified(true);
      if (type === "EMAIL") setEmailVerified(true);
      if (type === "WHATSAPP") setWhatsappVerified(true);

      message.success(
        type === "MOBILE"
          ? "Mobile number verified."
          : type === "EMAIL"
          ? "Email ID verified."
          : "WhatsApp number verified."
      );
    } catch (error) {
      message.error(error.message || "Unable to verify OTP.");
    }
  };

  const allRequiredVerified =
    mobileVerified &&
    emailVerified &&
    (whatsappSame || whatsappVerified);

  const createAccount = async () => {
    if (!allRequiredVerified) {
      message.error("Please complete all required OTP verifications.");
      return;
    }

    setSignupLoading(true);

    try {
      const data = await postJson("client_signup.php", {
        pan: pan.toUpperCase(),
        arn_no: selectedClient.arn_no,
        user_id: userId.trim(),
        password: signupPassword,
        confirm_password: confirmPassword,
        whatsapp_same_as_mobile: whatsappSame,
        whatsapp_number: whatsappSame ? "" : whatsappNumber.trim(),
      });

      if (data.status !== "ok") {
        message.error(data.message || "Unable to create account.");
        return;
      }
      setSignupSuccessPopup(true);
    } catch (error) {
      message.error(error.message || "Unable to create account.");
    } finally {
      setSignupLoading(false);
    }
  };

  const resetSignup = () => {
    setSignupStep(0);
    setPan("");
    setPanWarning("");
    setClients([]);
    setSelectedClient(null);
    setCustomUserId(false);
    setUserId("");
    setUserIdAvailable(false);
    setSignupPassword("");
    setConfirmPassword("");
    setWhatsappSame(true);
    setWhatsappNumber("");
    resetOtpState();
  };

  const otpRow = ({
    type,
    label,
    destination,
    sent,
    otp,
    setOtp,
    verified,
    icon,
  }) => (
    <div className="kg-otp-row">
      <div className="kg-contact-cell">
        <div className="kg-field-label">{label}</div>
        <Input
          size="large"
          prefix={icon}
          value={destination || ""}
          readOnly
        />
      </div>

      <Button
        size="large"
        className="kg-generate-btn"
        disabled={verified}
        onClick={() => sendOtp(type)}
      >
        {sent ? "Regenerate OTP" : "Generate OTP"}
      </Button>

      <Input
        size="large"
        className="kg-otp-input"
        inputMode="numeric"
        maxLength={6}
        placeholder={`Enter ${label} OTP`}
        value={otp}
        disabled={!sent || verified}
        onChange={(e) =>
          setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
        }
      />

      <Button
        size="large"
        className="kg-verify-btn"
        disabled={!sent || verified || otp.length !== 6}
        onClick={() => verifyOtp(type, otp)}
      >
        Verify
      </Button>

      <div className={`kg-verified-box ${verified ? "verified" : ""}`}>
        {verified && (
          <>
            <CheckCircleOutlined />
            <span>{label} Verified</span>
          </>
        )}
      </div>
    </div>
  );

  const signInContent = (
    <div className="kg-signin">
      <Title level={4}>
        <UserOutlined /> Customer Sign In
      </Title>

      {loginError && (
        <Alert
          className="kg-login-alert"
          message={loginError}
          type="error"
          showIcon
        />
      )}

      {biometricAvailable && biometricEnabled && !showCredentialLogin ? (
        <div style={{ textAlign: "center", paddingTop: 10 }}>
          <UnlockOutlined style={{ fontSize: 42, marginBottom: 12 }} />
          <Title level={4} style={{ marginBottom: 6 }}>Biometric Login</Title>
          <Text type="secondary">
            Authenticate with your device biometrics to securely sign in.
          </Text>
          <Button
            type="link"
            block
            disabled={biometricLoading}
            onClick={() => setShowCredentialLogin(true)}
            style={{ marginTop: 14 }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <>
          <Text type="secondary">
            Enter your credentials to access your portfolio.
          </Text>

          <Form
            className="kg-signin-form"
            layout="vertical"
            onFinish={handleSignIn}
            autoComplete="off"
          >
            <Form.Item
              label="User ID"
              name="user_id"
              normalize={(value) => String(value || "").toUpperCase()}
              rules={[{ required: true, message: "Please enter your User ID" }]}
            >
              <Input
                size="large"
                prefix={<UserOutlined />}
                placeholder="Enter User ID"
              />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, message: "Please enter your Password" }]}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="Enter Password"
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loginLoading}
            >
              Sign In
            </Button>

            {biometricAvailable && biometricEnabled && (
              <Button
                size="large"
                block
                icon={<UnlockOutlined />}
                loading={biometricLoading}
                onClick={() => handleBiometricLogin(false)}
                style={{ marginTop: 10 }}
              >
                Login with Biometrics
              </Button>
            )}

            <div className="kg-forgot-links">
              <Button type="link">Forgot User ID?</Button>
              <span>|</span>
              <Button type="link" onClick={openForgotPassword}>Forgot Password?</Button>
            </div>
          </Form>
        </>
      )}
    </div>
  );

  const signUpContent = (
    <div className="kg-signup-layout">
      <aside className="kg-step-sidebar">
        <Steps
          direction="vertical"
          current={signupStep}
          items={[
            { title: "Verify PAN" },
            { title: "Select Distributor" },
            { title: "Create User ID & Password" },
            { title: "Verify Contact Details" },
          ]}
        />

        <div className="kg-security-card">
          <SafetyOutlined />
          <div>
            <strong>Secure & Safe</strong>
            <span>Your information is protected.</span>
          </div>
        </div>
      </aside>

      <main className="kg-signup-main">
        <section className="kg-form-section">
          <div className="kg-section-heading">1. Verify PAN</div>

          <div className="kg-pan-row">
            <Input
              size="large"
              prefix={<IdcardOutlined />}
              placeholder="Enter PAN Number"
              value={pan}
              maxLength={10}
              disabled={signupStep > 0}
              onChange={(e) =>
                setPan(
                  e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, "")
                )
              }
              onPressEnter={verifyPan}
            />

            <Button
              type="primary"
              size="large"
              loading={panLoading}
              disabled={signupStep > 0}
              onClick={verifyPan}
            >
              Verify PAN
            </Button>
          </div>

          {panWarning && (
            <Alert
              type="error"
              showIcon
              message={panWarning}
              style={{ marginTop: 12 }}
            />
          )}
        </section>

        {signupStep >= 1 && (
          <section className="kg-form-section">
            <div className="kg-section-heading">2. Select Distributor</div>

            {clients.length > 1 ? (
              <Select
                size="large"
                className="kg-full-width"
                placeholder="Select Distributor (ARN)"
                value={selectedClient?.arn_no}
                onChange={handleDistributorSelect}
                options={clients.map((client) => ({
                  value: client.arn_no,
                  disabled: !!client.login_exists,
                  label:
                    `${client.distributor_name || client.arn_holder_name || "Distributor"} (${client.arn_no})` +
                    (client.login_exists ? " - Already Registered" : ""),
                }))}
              />
            ) : selectedClient ? (
              <div className="kg-selected-client">
                <strong>{selectedClient.client_name}</strong>
                <span>
                  {selectedClient.distributor_name ||
                    selectedClient.arn_holder_name ||
                    "Distributor"}{" "}
                  • {selectedClient.arn_no}
                </span>
              </div>
            ) : null}
          </section>
        )}

        {signupStep >= 2 && selectedClient && (
          <section className="kg-form-section">
            <div className="kg-section-heading">
              3. Create User ID & Password
            </div>

            <div className="kg-credential-grid">
              <div>
                <div className="kg-field-label">User ID</div>
                <Input
                  size="large"
                  value={userId}
                  disabled={!customUserId}
                  onChange={(e) => {
                    setUserId(e.target.value);
                    setUserIdAvailable(false);
                  }}
                />

                <Checkbox
                  className="kg-custom-user-checkbox"
                  checked={customUserId}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setCustomUserId(checked);
                    setUserId(checked ? "" : pan.toUpperCase());
                    setUserIdAvailable(false);
                  }}
                >
                  Create a different User ID
                </Checkbox>

                {customUserId && (
                  <div className="kg-userid-actions">
                    <Button
                      loading={userIdLoading}
                      onClick={() => checkUserId()}
                    >
                      Check Availability
                    </Button>

                    {userIdAvailable && (
                      <span className="kg-available">
                        <CheckCircleOutlined /> Available
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div>
                <div className="kg-field-label">Password</div>

                <Input.Password
                  size="large"
                  prefix={<LockOutlined />}
                  placeholder="Create Password"
                  value={signupPassword}
                  onChange={(e) => setSignupPassword(e.target.value)}
                />

                <Input.Password
                  className="kg-confirm-password"
                  size="large"
                  prefix={<LockOutlined />}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>

            {signupStep === 2 && (
              <Button
                type="primary"
                size="large"
                loading={userIdLoading}
                onClick={continueToContactVerification}
              >
                Continue to Contact Verification
              </Button>
            )}
          </section>
        )}

        {signupStep >= 3 && selectedClient && (
          <section className="kg-form-section">
            <div className="kg-section-heading">
              4. Verify Contact Details
            </div>

            {otpRow({
              type: "MOBILE",
              label: "Mobile Number",
              destination: selectedClient.masked_mobile,
              sent: mobileOtpSent,
              otp: mobileOtp,
              setOtp: setMobileOtp,
              verified: mobileVerified,
              icon: <PhoneOutlined />,
            })}

            {otpRow({
              type: "EMAIL",
              label: "Email ID",
              destination: selectedClient.masked_email,
              sent: emailOtpSent,
              otp: emailOtp,
              setOtp: setEmailOtp,
              verified: emailVerified,
              icon: <MailOutlined />,
            })}

            <div className="kg-whatsapp-choice">
              <Checkbox
                checked={whatsappSame}
                onChange={(e) => {
                  setWhatsappSame(e.target.checked);
                  setWhatsappNumber("");
                  setWhatsappOtp("");
                  setWhatsappOtpSent(false);
                  setWhatsappVerified(false);
                }}
              >
                WhatsApp number is same as registered mobile
              </Checkbox>
            </div>

            {!whatsappSame && (
              <>
                <div className="kg-whatsapp-number">
                  <div className="kg-field-label">WhatsApp Number</div>
                  <Input
                    size="large"
                    prefix={<WhatsAppOutlined />}
                    placeholder="Enter WhatsApp Number"
                    value={whatsappNumber}
                    maxLength={15}
                    onChange={(e) => {
                      setWhatsappNumber(
                        e.target.value.replace(/[^\d+]/g, "")
                      );
                      setWhatsappOtpSent(false);
                      setWhatsappVerified(false);
                    }}
                  />
                </div>

                {otpRow({
                  type: "WHATSAPP",
                  label: "WhatsApp Number",
                  destination: whatsappNumber,
                  sent: whatsappOtpSent,
                  otp: whatsappOtp,
                  setOtp: setWhatsappOtp,
                  verified: whatsappVerified,
                  icon: <WhatsAppOutlined />,
                })}
              </>
            )}

            <Alert
              className="kg-verification-alert"
              type={allRequiredVerified ? "success" : "info"}
              showIcon
              message={
                allRequiredVerified
                  ? "All required verifications completed. You can now create your account."
                  : "Mobile and Email verification are mandatory. Verify WhatsApp also if it is different from Mobile."
              }
            />

            <Button
              type="primary"
              size="large"
              block
              loading={signupLoading}
              disabled={!allRequiredVerified}
              onClick={createAccount}
            >
              Create Account
            </Button>
          </section>
        )}

        {signupStep > 0 && (
          <Button type="link" onClick={resetSignup}>
            Start Again / Change PAN
          </Button>
        )}
      </main>
    </div>
  );

  const content = (
    <div className="kg-client-login">
      {!embedded && (
        <div className="kg-client-brand">
          <SafetyOutlined />
          <Title level={2}>KGOnline</Title>
          <Text type="secondary">Client Portfolio Access</Text>
        </div>
      )}

      <Tabs
        className="kg-client-tabs"
        centered
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "signin",
            label: "Sign In",
            children: signInContent,
          },
          {
            key: "signup",
            label: "Sign Up",
            children: signUpContent,
          },
        ]}
      />


      <Modal
        open={!!activeSessionPrompt}
        centered
        title="Already Logged In"
        okText="Logout Previous & Continue"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        confirmLoading={loginLoading}
        closable={!loginLoading}
        maskClosable={false}
        keyboard={!loginLoading}
        onCancel={() => {
          if (!loginLoading) setActiveSessionPrompt(null);
        }}
        onOk={forceLoginFromPrompt}
      >
        <Alert
          type="warning"
          showIcon
          message="Active session detected"
          description={
            activeSessionPrompt?.message ||
            "Your account is already logged in on another device."
          }
        />
      </Modal>

      <Modal
        open={!!biometricOffer}
        centered
        closable={false}
        maskClosable={false}
        keyboard={false}
        title="Enable Biometric Login?"
        footer={[
          <Button key="later" disabled={biometricLoading} onClick={() => {
            const offer = biometricOffer;
            setBiometricOffer(null);
            if (offer) finishClientLogin(offer.token, offer.userId, offer.displayName);
          }}>
            Not Now
          </Button>,
          <Button key="enable" type="primary" loading={biometricLoading} onClick={acceptBiometricOffer}>
            Enable
          </Button>,
        ]}
      >
        <Text>Use your device biometrics for faster secure sign-in on this mobile device. Your login password will not be stored.</Text>
      </Modal>

      <Modal
        open={forgotOpen}
        centered
        width={520}
        closable={!forgotLoading}
        maskClosable={false}
        keyboard={!forgotLoading}
        destroyOnHidden
        title="Forgot Password"
        footer={null}
        onCancel={closeForgotPassword}
      >
        <div style={{ paddingTop: 4 }}>
          <Steps
            size="small"
            current={Math.min(forgotStep, 2)}
            items={[
              { title: "Verify Account" },
              { title: "Verify OTP" },
              { title: "New Password" },
            ]}
            style={{ marginBottom: 24 }}
          />

          {forgotError && (
            <Alert
              type="error"
              showIcon
              message={forgotError}
              style={{ marginBottom: 16 }}
            />
          )}

          {forgotStep === 0 && (
            <>
              <Text type="secondary">
                Enter your User ID and choose where you want to receive the
                password reset OTP.
              </Text>

              <div style={{ marginTop: 18 }}>
                <div className="kg-field-label">User ID</div>
                <Input
                  size="large"
                  prefix={<UserOutlined />}
                  placeholder="Enter User ID"
                  value={forgotUserId}
                  maxLength={50}
                  onChange={(e) => {
                    setForgotUserId(e.target.value.toUpperCase());
                    setForgotError("");
                  }}
                  onPressEnter={() => sendForgotOtp()}
                />
              </div>

              <div style={{ marginTop: 16 }}>
                <div className="kg-field-label">Send OTP To</div>
                <Select
                  size="large"
                  style={{ width: "100%" }}
                  value={forgotMethod}
                  onChange={(value) => {
                    setForgotMethod(value);
                    setForgotError("");
                  }}
                  options={[
                    {
                      value: "BOTH",
                      label: "Both - Registered Mobile & Email",
                    },
                    {
                      value: "MOBILE",
                      label: "Registered Mobile Only",
                    },
                    {
                      value: "EMAIL",
                      label: "Registered Email Only",
                    },
                  ]}
                />
              </div>

              <Button
                type="primary"
                size="large"
                block
                loading={forgotLoading}
                onClick={() => sendForgotOtp()}
                style={{ marginTop: 22 }}
              >
                Send OTP
              </Button>
            </>
          )}

          {forgotStep === 1 && (
            <>
              <Text type="secondary">
                Enter the 6-digit OTP sent to your registered contact
                {forgotDestination ? ` (${forgotDestination})` : ""}.
              </Text>

              <Input
                size="large"
                prefix={<SafetyOutlined />}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="Enter 6-digit OTP"
                value={forgotOtp}
                onChange={(e) => {
                  setForgotOtp(
                    e.target.value.replace(/\D/g, "").slice(0, 6)
                  );
                  setForgotError("");
                }}
                onPressEnter={verifyForgotOtp}
                style={{ marginTop: 18 }}
              />

              <Button
                type="primary"
                size="large"
                block
                loading={forgotLoading}
                disabled={forgotOtp.length !== 6}
                onClick={verifyForgotOtp}
                style={{ marginTop: 16 }}
              >
                Verify OTP
              </Button>

              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  type="link"
                  style={{ paddingLeft: 0 }}
                  disabled={forgotLoading || forgotResendSeconds > 0}
                  onClick={() => sendForgotOtp({ resend: true })}
                >
                  {forgotResendSeconds > 0
                    ? `Resend OTP in ${forgotResendSeconds}s`
                    : "Resend OTP"}
                </Button>

                <Button
                  type="link"
                  disabled={forgotLoading}
                  onClick={() => {
                    setForgotStep(0);
                    setForgotRequestId(null);
                    setForgotDestination("");
                    setForgotOtp("");
                    setForgotResetToken("");
                    setForgotResendSeconds(0);
                    setForgotError("");
                  }}
                >
                  Change Method
                </Button>
              </div>
            </>
          )}

          {forgotStep === 2 && (
            <>
              <Alert
                type="success"
                showIcon
                message="OTP verified. Create your new password."
                style={{ marginBottom: 16 }}
              />

              <div className="kg-field-label">New Password</div>
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="Enter New Password"
                value={forgotNewPassword}
                autoComplete="new-password"
                onChange={(e) => {
                  setForgotNewPassword(e.target.value);
                  setForgotError("");
                }}
              />

              <div className="kg-field-label" style={{ marginTop: 14 }}>
                Confirm New Password
              </div>
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="Confirm New Password"
                value={forgotConfirmPassword}
                autoComplete="new-password"
                onChange={(e) => {
                  setForgotConfirmPassword(e.target.value);
                  setForgotError("");
                }}
                onPressEnter={completeForgotPassword}
              />

              <Text
                type="secondary"
                style={{ display: "block", marginTop: 10, fontSize: 12 }}
              >
                Minimum 8 characters with uppercase, lowercase, number and
                special character.
              </Text>

              <Button
                type="primary"
                size="large"
                block
                loading={forgotLoading}
                onClick={completeForgotPassword}
                style={{ marginTop: 18 }}
              >
                Change Password
              </Button>
            </>
          )}

          {forgotStep === 3 && forgotSuccess && (
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <CheckCircleOutlined
                style={{ fontSize: 48, marginBottom: 14 }}
              />
              <Title level={4}>Password Changed Successfully</Title>
              <Text type="secondary">
                Your password has been changed. Please sign in using your new
                password.
              </Text>

              <Button
                type="primary"
                size="large"
                block
                style={{ marginTop: 22 }}
                onClick={() => {
                  closeForgotPassword();
                  setLoginError("");
                  setActiveTab("signin");
                }}
              >
                Go to Sign In
              </Button>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={signupSuccessPopup}
        centered
        closable={false}
        maskClosable={false}
        keyboard={false}
        title="Account Created Successfully"
        okText="Sign In"
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={() => {
          setSignupSuccessPopup(false);
          resetSignup();
          setLoginError("");
          setActiveTab("signin");
        }}
      >
        <div style={{ fontSize: 16, lineHeight: 1.7 }}>
          Your KGOnline Client account has been created successfully.
          Please Sign In using your User ID and Password.
        </div>
      </Modal>

      <Modal
        open={existingLoginPopup.open}
        centered
        closable={false}
        maskClosable={false}
        keyboard={false}
        title="Account Already Registered"
        okText="Go to Sign In"
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={() => {
          setExistingLoginPopup({ open: false, clientName: "" });
          resetSignup();
          setLoginError("");
          setActiveTab("signin");
        }}
      >
        <div style={{ fontSize: 16, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Hey!!! {existingLoginPopup.clientName || "Client"}
          </div>
          <div>
            Your Login Profile is already created. Please Sign In using your existing User ID and Password.
          </div>
        </div>
      </Modal>

    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="kg-client-standalone">
      {content}
    </div>
  );
}

export default ClientLogin;
